import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { rolesAPI, usersAPI } from '../utils/api';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { usePermissions } from '../hooks/usePermissions';
import type { PermissionInfo } from '../types';
import RolePermissionsModal from '../components/RolePermissionsModal';

// Role labels/descriptions used as a fallback when the backend doesn't provide them
const ROLE_LABELS: Record<string, string> = {
  admin: 'Amministratore',
  manager: 'Manager',
  commerciale: 'Commerciale',
  viewer: 'Visualizzatore',
};

const ROLE_DESCRIPTIONS: Record<string, string> = {
  admin: 'Accesso completo a tutte le funzionalità del sistema, inclusa la gestione utenti e configurazioni',
  manager: 'Gestione completa di offerte, clienti e prodotti. Può visualizzare report e statistiche',
  commerciale: 'Creazione e modifica delle proprie offerte, accesso a clienti e prodotti',
  viewer: 'Solo visualizzazione di offerte, clienti e prodotti. Nessuna modifica consentita',
};

const ROLE_CARD_COLORS: Record<string, string> = {
  admin: 'border-purple-200 hover:border-purple-300',
  manager: 'border-blue-200 hover:border-blue-300',
  commerciale: 'border-green-200 hover:border-green-300',
  viewer: 'border-gray-200 hover:border-gray-300',
};

const ROLE_BADGE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  manager: 'bg-blue-100 text-blue-800',
  commerciale: 'bg-green-100 text-green-800',
  viewer: 'bg-gray-100 text-gray-800',
};

const Roles = () => {
  const queryClient = useQueryClient();
  const { canManageUsers } = usePermissions();
  const [selectedRoleKey, setSelectedRoleKey] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPermissions, setEditedPermissions] = useState<string[]>([]);

  // Fetch all roles from database
  const { data: rolesData, isLoading } = useQuery({
    queryKey: ['roles'],
    queryFn: async () => {
      const response = await rolesAPI.getAllRoles();
      return response.data;
    },
  });

  // Fetch permissions metadata for display
  const { data: permissionsMetadata } = useQuery({
    queryKey: ['permissions-metadata'],
    queryFn: async () => {
      const response = await usersAPI.getRolesPermissions();
      return response.data;
    },
  });

  // Group permissions by category, preserving the first-seen order from the API
  const { categoriesMap, orderedCategories } = useMemo(() => {
    const map: Record<string, PermissionInfo[]> = {};
    const order: string[] = [];

    (permissionsMetadata?.all_permissions || []).forEach((perm) => {
      if (!map[perm.category]) {
        map[perm.category] = [];
        order.push(perm.category);
      }
      map[perm.category].push(perm);
    });

    return { categoriesMap: map, orderedCategories: order };
  }, [permissionsMetadata]);

  // Update role permissions mutation
  const updateMutation = useMutation({
    mutationFn: ({ role, permissions }: { role: string; permissions: string[] }) =>
      rolesAPI.updateRolePermissions(role, { permissions }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['roles'] });
      queryClient.invalidateQueries({ queryKey: ['permissions-metadata'] });
      toast.success('Permessi del ruolo aggiornati con successo');
      setIsEditing(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Errore durante l\'aggiornamento');
    },
  });

  const roles = rolesData || {};

  const handleOpenRole = (roleKey: string, permissions: string[]) => {
    setSelectedRoleKey(roleKey);
    setIsEditing(false);
    setEditedPermissions([...permissions]);
  };

  const handleCloseModal = () => {
    setSelectedRoleKey(null);
    setIsEditing(false);
    setEditedPermissions([]);
  };

  const handleEditStart = () => setIsEditing(true);

  const handleEditCancel = () => {
    if (selectedRoleKey) {
      setEditedPermissions([...(roles[selectedRoleKey]?.permissions || [])]);
    }
    setIsEditing(false);
  };

  const handleTogglePermission = (key: string) => {
    setEditedPermissions((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key]
    );
  };

  const handleToggleCategoryPermissions = (category: string, checked: boolean) => {
    const keysInCategory = (categoriesMap[category] || []).map((p) => p.key);
    setEditedPermissions((prev) => {
      const set = new Set(prev);
      keysInCategory.forEach((key) => {
        if (checked) set.add(key);
        else set.delete(key);
      });
      return Array.from(set);
    });
  };

  const handleSave = () => {
    if (!selectedRoleKey) return;
    updateMutation.mutate({ role: selectedRoleKey, permissions: editedPermissions });
  };

  if (isLoading || !permissionsMetadata) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const selectedRoleData = selectedRoleKey ? roles[selectedRoleKey] : null;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Ruoli e Permessi</h1>
        <p className="text-gray-600 mt-1">
          Panoramica dei ruoli disponibili e dei loro permessi
        </p>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <ShieldCheckIcon className="h-5 w-5 text-blue-600 mr-3 mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-medium text-blue-900">Sistema RBAC (Role-Based Access Control)</h3>
            <p className="text-sm text-blue-700 mt-1">
              Il sistema utilizza ruoli predefiniti con permessi specifici. Clicca su un ruolo per aprire l'albero
              completo dei permessi assegnati. Puoi personalizzare i permessi per singoli utenti aggiungendo permessi
              extra o negando permessi specifici dalla pagina Utenti.
            </p>
          </div>
        </div>
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {Object.entries(roles).map(([roleKey, roleData]: [string, any]) => {
          const permissionCount = (roleData.permissions || []).length;

          return (
            <button
              key={roleKey}
              onClick={() => handleOpenRole(roleKey, roleData.permissions || [])}
              className={`text-left bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-all ${
                ROLE_CARD_COLORS[roleKey] || 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center justify-between mb-3">
                <div
                  className={`h-10 w-10 rounded-full flex items-center justify-center ${
                    ROLE_BADGE_COLORS[roleKey] || 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <ShieldCheckIcon className="h-5 w-5" />
                </div>
                <span
                  className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    ROLE_BADGE_COLORS[roleKey] || 'bg-gray-100 text-gray-800'
                  }`}
                >
                  {permissionCount} {permissionCount === 1 ? 'permesso' : 'permessi'}
                </span>
              </div>
              <h2 className="text-base font-semibold text-gray-900">
                {roleData.label || ROLE_LABELS[roleKey] || roleKey}
              </h2>
              <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                {roleData.description || ROLE_DESCRIPTIONS[roleKey] || 'Nessuna descrizione disponibile'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Role Permissions Modal */}
      {selectedRoleKey && selectedRoleData && (
        <RolePermissionsModal
          isOpen
          onClose={handleCloseModal}
          roleKey={selectedRoleKey}
          roleLabel={selectedRoleData.label || ROLE_LABELS[selectedRoleKey] || selectedRoleKey}
          roleDescription={selectedRoleData.description || ROLE_DESCRIPTIONS[selectedRoleKey]}
          orderedCategories={orderedCategories}
          categoriesMap={categoriesMap}
          grantedPermissions={selectedRoleData.permissions || []}
          isEditing={isEditing}
          editedPermissions={editedPermissions}
          canEdit={canManageUsers}
          isSaving={updateMutation.isPending}
          onEditStart={handleEditStart}
          onEditCancel={handleEditCancel}
          onSave={handleSave}
          onTogglePermission={handleTogglePermission}
          onToggleCategoryPermissions={handleToggleCategoryPermissions}
        />
      )}

      {/* All Permissions Reference */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-medium text-gray-900">Riferimento Completo Permessi</h2>
          <p className="text-sm text-gray-600 mt-1">
            Tutti i permessi disponibili nel sistema organizzati per categoria
          </p>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {orderedCategories.map((category) => (
              <div key={category}>
                <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-2 border-b border-gray-200">
                  {category}
                </h3>
                <div className="space-y-2">
                  {categoriesMap[category].map((perm) => (
                    <div key={perm.key} className="text-sm">
                      <p className="font-medium text-gray-700">{perm.name}</p>
                      {perm.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{perm.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Roles;
